import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { motion } from "framer-motion";
import {
  Play, Lock, DollarSign, BarChart3, Link2, Gift, Upload, ArrowRight, Film,
} from "lucide-react";

const HERO =
  "https://images.pexels.com/photos/9665187/pexels-photo-9665187.jpeg";

const FEATURES = [
  { icon: Upload, title: "One vault per event", desc: "Bulk-upload every clip from a shoot into a single private, streamable collection." },
  { icon: Play, title: "Stream before buying", desc: "Clients preview every video in a cinematic player — downloads stay locked until unlocked." },
  { icon: Gift, title: "Complimentary picks", desc: "Include free downloads (default 5). Clients spend them on any clips they love." },
  { icon: DollarSign, title: "Per-clip pricing", desc: "Price each video individually. Extra clips are purchased instantly through Stripe." },
  { icon: Link2, title: "Private share links", desc: "Publish and share one secret link. No accounts required for your clients." },
  { icon: BarChart3, title: "Revenue analytics", desc: "Track revenue, sales, redeemed freebies, views and downloads per event." },
];

export default function Landing() {
  const { user } = useAuth();
  const ctaTo = user ? "/dashboard" : "/register";

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />

      {/* Hero */}
      <section className="relative grain overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/80 to-[#050505]/40" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 70% 30%, rgba(0,240,255,0.12), transparent 55%)" }} />

        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 min-h-[88vh] flex flex-col justify-center py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-white/70 backdrop-blur-md">
              <Film className="h-3.5 w-3.5 text-[#00F0FF]" /> For videographers & creators
            </span>
            <h1 className="font-display mt-6 text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tighter leading-[0.95]">
              Deliver your footage.
              <br />
              <span className="text-[#00F0FF]">Clients choose their favorites.</span>
            </h1>
            <p className="mt-6 text-lg text-white/60 max-w-xl leading-relaxed">
              Replace messy file links with a beautiful client gallery where every clip is
              organized, shareable, and ready to unlock.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to={ctaTo}
                data-testid="hero-cta"
                className="group inline-flex items-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-7 py-3.5 hover:scale-105 transition-transform duration-300 cyan-glow"
              >
                {user ? "Go to Dashboard" : "Create your first vault"}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 border border-white/20 text-white rounded-full px-7 py-3.5 hover:bg-white/10 transition-colors"
              >
                Sign in
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-24">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.25em] text-[#00F0FF]">How it works</p>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
            Everything you need to monetize a shoot
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              data-testid={`feature-${i}`}
              className="group rounded-2xl border border-white/10 bg-[#0d0d0d] p-7 hover:border-[#00F0FF]/40 transition-colors"
            >
              <span className="grid place-items-center h-12 w-12 rounded-xl bg-[#00F0FF]/10 text-[#00F0FF] group-hover:bg-[#00F0FF] group-hover:text-black transition-colors">
                <f.icon className="h-6 w-6" strokeWidth={1.5} />
              </span>
              <h3 className="font-display mt-5 text-xl font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-white/50 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pb-28">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#0d0d0d] to-black p-6 sm:p-10 md:p-16">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 85% 20%, rgba(0,240,255,0.15), transparent 50%)" }} />
          <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight max-w-xl">
                Ready to turn footage you already shot into income?
              </h2>
              <p className="mt-3 text-white/50 flex items-center gap-2">
                <Lock className="h-4 w-4 shrink-0 text-[#00F0FF]" /> Private, secure, and pay-as-you-grow.
              </p>
            </div>
            <Link
              to={ctaTo}
              className="w-full md:w-auto shrink-0 inline-flex items-center justify-center gap-2 whitespace-nowrap bg-[#00F0FF] text-black font-semibold rounded-full px-8 py-4 hover:scale-105 transition-transform duration-300 cyan-glow"
            >
              Get started free <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/40">
          <span className="font-display font-semibold text-white">Narrative<span className="text-[#00F0FF]">Vault</span></span>
          <div className="flex items-center gap-6">
            <Link to="/faq" data-testid="footer-faq-link" className="hover:text-white transition-colors">FAQ & Pricing</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/copyright" data-testid="footer-copyright-link" className="hover:text-white transition-colors">Copyright</Link>
            <span>© {new Date().getFullYear()} — by WTN Productions</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
